import type { ModpackConfig } from '../../types/modpackConfig';

/**
 * Field metadata shared by the create and edit forms.
 *
 * The edit form sends only what changed, so it needs to know each field's type
 * to compare a form value against the row it came from — `"25575"` from an
 * input is not a change to `25575` in the database.
 *
 * Several fields carry operational rules that are not obvious from the column
 * name, and getting them wrong takes a server off the network. Those rules are
 * encoded here — as derivations, validators and suggestions — rather than left
 * to whoever is filling the form in.
 */

export type FieldKind =
  | 'text'
  | 'number'
  | 'url'
  | 'select'
  | 'combobox'
  | 'ip'
  | 'server_picker'
  | 'timestamp';

export interface FieldSpec {
  key: keyof ModpackConfig;
  label: string;
  kind: FieldKind;
  /** Short line under the input. */
  help?: string;
  /** Requires `argus.modpackconfigs.admin`. */
  adminOnly?: boolean;
  /** Values come from the data, via the vocabularies endpoint. */
  vocabulary?: 'regions' | 'modloaders';
  placeholder?: string;
  /** What an empty timestamp means, e.g. "Never wiped". */
  emptyLabel?: string;
}

// --- the operational rules --------------------------------------------------

/**
 * RCON ports follow the query port: `88` plus the query port's last two digits.
 * 25565 → 8865, 25576 → 8876, 45570 → 8870. Forty-four of the forty-nine live
 * rows follow this exactly; the handful that do not are why it is a suggestion
 * the form fills in and warns about, never a rule it enforces.
 */
export const deriveRconPort = (queryPort: number): number | null =>
  Number.isInteger(queryPort) && queryPort > 0 ? 8800 + (queryPort % 100) : null;

/**
 * Tailscale's CGNAT range, 100.64.0.0/10. RCON is reached over the tailnet;
 * a public address here would expose the server's RCON port to anyone who can
 * read the authenticated API. The backend enforces the same check.
 */
export function isTailscaleIp(value: string): boolean {
  const parts = value.trim().split('.');
  if (parts.length !== 4) return false;
  const octets = parts.map((p) => (/^\d{1,3}$/.test(p) ? Number(p) : NaN));
  if (octets.some((o) => !Number.isInteger(o) || o < 0 || o > 255)) return false;
  return octets[0] === 100 && octets[1] >= 64 && octets[1] <= 127;
}

/**
 * Keep the RCON port in step with the query port while the user has not set
 * it by hand. "By hand" is detected by whether the current RCON value is what
 * the previous query port would have derived — if it is, the user never
 * touched it and the new derivation replaces it; if not, it is left alone.
 */
export function applyPortConvention(
  prev: Record<string, string>,
  key: string,
  value: string,
): Record<string, string> {
  const next = { ...prev, [key]: value };
  if (key !== 'query_port') return next;

  const derivedFromPrevious = deriveRconPort(Number(prev.query_port));
  const rconWasAutomatic =
    (prev.rcon_port ?? '') === '' ||
    (derivedFromPrevious !== null && prev.rcon_port === String(derivedFromPrevious));

  if (rconWasAutomatic) {
    const derived = deriveRconPort(Number(value));
    next.rcon_port = derived === null ? '' : String(derived);
  }
  return next;
}

export interface FieldMessage {
  key: string;
  message: string;
}

/**
 * Errors block submission; warnings are shown and let through. The only hard
 * error is the IP range, because the consequence of a public address is
 * exposure rather than inconvenience.
 */
export function validateConfigValues(values: Record<string, string>): {
  errors: FieldMessage[];
  warnings: FieldMessage[];
} {
  const errors: FieldMessage[] = [];
  const warnings: FieldMessage[] = [];

  const ip = (values.ip_address ?? '').trim();
  if (ip && !isTailscaleIp(ip)) {
    errors.push({
      key: 'ip_address',
      message: 'Must be the Tailscale address (100.64.x.x – 100.127.x.x), never the public IP.',
    });
  }

  const query = Number(values.query_port);
  const rcon = Number(values.rcon_port);
  const derived = deriveRconPort(query);
  if (values.query_port && values.rcon_port && derived !== null && rcon !== derived) {
    warnings.push({
      key: 'rcon_port',
      message: `Does not follow the usual pattern — a query port of ${query} normally means RCON ${derived}. Fine if this server is the exception.`,
    });
  }

  return { errors, warnings };
}

// --- the fields ---------------------------------------------------------------

export const IDENTITY_FIELDS: FieldSpec[] = [
  { key: 'modpack_full_name', label: 'Display name', kind: 'text', placeholder: 'All The Mods 10', help: 'Shown in announcements and used to name the Discord role.' },
  { key: 'modpack_name', label: 'Short name', kind: 'text', placeholder: 'atm10' },
  { key: 'modpack_version', label: 'Pack version', kind: 'text', placeholder: '1.1.4' },
  { key: 'author', label: 'Author', kind: 'text' },
  { key: 'modloader', label: 'Modloader', kind: 'select', vocabulary: 'modloaders' },
  { key: 'server_download_url', label: 'Download URL', kind: 'url', placeholder: 'https://www.curseforge.com/minecraft/modpacks/…' },
  { key: 'thumbnail_url', label: 'Thumbnail URL', kind: 'url', help: 'Used as the announcement thumbnail.' },
];

export const NETWORK_FIELDS: FieldSpec[] = [
  {
    key: 'ip_address', label: 'Tailscale IP', kind: 'ip', adminOnly: true,
    placeholder: '100.64.x.x',
    help: 'The server\'s address on the tailnet, always 100.64–100.127. Never the public IP: RCON is reached over Tailscale, and a public address here would expose it. Used for the RCON fallback when a Redis dispatch goes unacknowledged.',
  },
  {
    key: 'query_port', label: 'Query port', kind: 'number', adminOnly: true,
    placeholder: '25565',
    help: 'The port players connect on — 25565, 25576 and so on. Typing it fills in the RCON port below.',
  },
  {
    key: 'rcon_port', label: 'RCON port', kind: 'number', adminOnly: true,
    placeholder: '8865',
    help: 'By convention 88 plus the query port\'s last two digits: 25565 → 8865, 25576 → 8876. Filled in automatically; change it only if this server breaks the pattern.',
  },
  {
    key: 'amp_name', label: 'AMP instance', kind: 'text', adminOnly: true,
    help: 'The AMP instance name restarts are sent to — a wrong value restarts the wrong server. Must match the instance exactly as AMP names it.',
  },
  {
    key: 'region_name', label: 'Region', kind: 'combobox', vocabulary: 'regions',
    placeholder: 'US, EU, AU, US1…',
    help: 'Usually a region indicator, but it can be anything — suggestions come from what is already in use.',
  },
];

export const CLUSTER_FIELDS: FieldSpec[] = [
  {
    key: 'cluster_name', label: 'Cluster name', kind: 'text', placeholder: 'ATM10',
    help: 'Shared by every server running this pack. Use "default" for a pack that belongs to no cluster.',
  },
  {
    key: 'cluster_parent', label: 'Cluster head', kind: 'server_picker',
    help: 'The server that heads this cluster — the Discord role and forum are keyed on it. A new pack heads its own cluster unless you pick an existing server to join.',
  },
];

export const INDEX_FIELDS: FieldSpec[] = [
  { key: 'modpack_index_id', label: 'Modpack Index id', kind: 'number', help: 'Joins this server to the pack catalogue and drives update detection.' },
  { key: 'ftb_id', label: 'FTB id', kind: 'number' },
  {
    key: 'date_wiped', label: 'Last world wipe', kind: 'timestamp', emptyLabel: 'Never wiped',
    help: 'When this pack\'s world was last wiped — players are shown this. A wipe is recorded on the whole cluster from the Cluster tab; edit here only to correct a date.',
  },
  {
    key: 'date_added', label: 'Added', kind: 'timestamp', adminOnly: true, emptyLabel: 'Unknown',
    help: 'When the pack was created. Set automatically; some older rows predate it.',
  },
];

export const ALL_EDITABLE_FIELDS: FieldSpec[] = [
  ...IDENTITY_FIELDS,
  ...NETWORK_FIELDS,
  ...CLUSTER_FIELDS,
  ...INDEX_FIELDS,
];

/** Unix seconds of the last wipe, or null for a pack that has never been wiped (0 and null both mean never). */
export const lastWipe = (config: Pick<ModpackConfig, 'date_wiped'>): number | null =>
  typeof config.date_wiped === 'number' && config.date_wiped > 0 ? config.date_wiped : null;

/** Whether staff have withdrawn the server from the public API. */
export const isHidden = (config: Pick<ModpackConfig, 'hidden'>): boolean => Number(config.hidden) === 1;

/** What the form shows for a value that may be null. */
export const toFormValue = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

/**
 * The same, but aware of the field: a timestamp of 0 is the table's "never",
 * and a form must not show it as 1 January 1970.
 */
export const toFormValueFor = (spec: FieldSpec, value: unknown): string =>
  spec.kind === 'timestamp' && (value === 0 || value === null || value === undefined)
    ? ''
    : toFormValue(value);

// --- timestamps as people see them ------------------------------------------
//
// The columns hold unix seconds. The form works in the browser's local time
// through a datetime-local input, whose value has no zone and is read back as
// local time by Date.parse, so the round trip is exact to the minute.

const pad = (n: number) => String(n).padStart(2, '0');

/** Unix seconds → the `YYYY-MM-DDTHH:mm` a datetime-local input takes, in local time. */
export function secondsToLocalInput(seconds: number | null | undefined): string {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return '';
  const d = new Date(seconds * 1000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The input's value → unix seconds, or null when empty or unparseable. */
export function localInputToSeconds(value: string): number | null {
  if (!value.trim()) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

/**
 * Turn a form string back into what the API expects, or `undefined` when it is
 * unchanged. Returning `undefined` is what keeps a submission to only the
 * fields the user actually touched.
 */
export function toPatchValue(
  spec: FieldSpec,
  raw: string,
  original: unknown,
): { changed: boolean; value: string | number | null } {
  const trimmed = raw.trim();

  if (trimmed === '') {
    if (spec.kind === 'timestamp') {
      // 0 is the table's "never" for timestamps (Mr. Roboto writes it on
      // create), so clearing one writes 0 and clearing an already-empty one
      // is not a change.
      const empty = original === null || original === undefined || original === 0;
      return { changed: !empty, value: 0 };
    }
    // An emptied field clears the column rather than writing an empty string,
    // which is what every other consumer treats as absent.
    return { changed: original !== null && original !== undefined, value: null };
  }

  if (spec.kind === 'number' || spec.kind === 'timestamp' || spec.kind === 'server_picker') {
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return { changed: false, value: null };
    return { changed: parsed !== original, value: parsed };
  }

  return { changed: trimmed !== original, value: trimmed };
}
